import { AxiosResponse, isAxiosError } from "axios"

const FILENAME_STAR = /filename\*\s*=\s*UTF-8''([^;]+)/i
const FILENAME_QUOTED = /filename\s*=\s*"([^"]*)"/i
const FILENAME_BARE = /filename\s*=\s*([^;]+)/i

/**
 * Имя файла выгрузки задаёт сервер (`Content-Disposition`). Клиент его не
 * придумывает: fallback используется только когда заголовок недоступен —
 * например, когда CORS не выставил `Content-Disposition` наружу.
 */
export const resolveAttachmentFileName = (
    contentDisposition: string | undefined,
    fallback: string
): string => {
    if (!contentDisposition) return fallback

    const encoded = FILENAME_STAR.exec(contentDisposition)
    if (encoded?.[1]) {
        try {
            const decoded = decodeURIComponent(encoded[1].trim())
            if (decoded) return decoded
        } catch {
            // Некорректная percent-последовательность — падать нельзя.
        }
    }

    const quoted = FILENAME_QUOTED.exec(contentDisposition)
    if (quoted?.[1]?.trim()) return quoted[1].trim()

    const bare = FILENAME_BARE.exec(contentDisposition)
    if (bare?.[1]?.trim()) return bare[1].trim()

    return fallback
}

const readHeader = (response: AxiosResponse<Blob>, name: string): string | undefined => {
    const headers = response.headers as unknown as (
        Record<string, unknown> & { get?: (headerName: string) => unknown }
    ) | undefined
    const fromGetter = headers?.get?.(name)
    if (typeof fromGetter === "string") return fromGetter

    const headerKey = Object.keys(headers ?? {}).find(
        (key) => key.toLowerCase() === name.toLowerCase()
    )
    const value = headerKey ? headers?.[headerKey] : undefined

    return typeof value === "string" ? value : undefined
}

/**
 * Вызывается ТОЛЬКО для успешного ответа. Ошибка не должна порождать ни
 * частичный, ни пустой файл, поэтому вызывающий код обязан держать этот вызов
 * внутри ветки успеха, а не в `finally`.
 */
export const downloadAttachment = (
    response: AxiosResponse<Blob>,
    fallbackName = response.data.type.includes("spreadsheetml")
        ? "export.xlsx"
        : response.data.type.includes("csv") ? "export.csv" : "download"
): void => {
    if (response.status < 200 || response.status >= 300) return

    const fileName = resolveAttachmentFileName(
        readHeader(response, "content-disposition"),
        fallbackName
    )
    const url = URL.createObjectURL(response.data)
    const link = document.createElement("a")

    link.href = url
    link.download = fileName
    link.rel = "noopener"
    link.style.display = "none"
    document.body.appendChild(link)

    try {
        link.click()
    } finally {
        link.remove()
        URL.revokeObjectURL(url)
    }
}

const isBlob = (value: unknown): value is Blob =>
    typeof Blob !== "undefined" && value instanceof Blob

/**
 * `Blob.text()` есть в браузерах, но не во всех окружениях (в частности, его
 * нет у jsdom), поэтому при отсутствии метода читаем через `FileReader` —
 * иначе разбор тела ошибки молча ломался бы там, где его и проверяют.
 */
export const readBlobText = (blob: Blob): Promise<string> => {
    if (typeof blob.text === "function") return blob.text()

    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.addEventListener("load", () => resolve(String(reader.result ?? "")))
        reader.addEventListener("error", () => reject(reader.error))
        reader.readAsText(blob)
    })
}

/**
 * При `responseType: "blob"` axios отдаёт тело ошибки тоже как Blob, поэтому
 * `error_id` из документированного конверта `{message,error_id}` приходится
 * доставать вручную. Функция никогда не бросает: непрочитанное или не-JSON
 * тело означает "идентификатор неизвестен", а не сбой обработки ошибки.
 */
export const resolveBlobErrorId = async (error: unknown): Promise<string | undefined> => {
    const payload = isAxiosError(error) ? error.response?.data : undefined

    try {
        if (isBlob(payload)) {
            const text = await readBlobText(payload)
            const parsed: unknown = JSON.parse(text)

            if (typeof parsed !== "object" || parsed === null) return undefined
            const errorId = (parsed as { error_id?: unknown }).error_id

            return typeof errorId === "string" ? errorId : undefined
        }

        if (typeof payload === "object" && payload !== null) {
            const errorId = (payload as { error_id?: unknown }).error_id

            return typeof errorId === "string" ? errorId : undefined
        }
    } catch {
        return undefined
    }

    return undefined
}
